/**
 * @fileoverview Página responsável por gerenciar publicações de novidades.
 */

import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import { Pencil, Trash2 as Trash, Copy, Plus, Search } from "lucide-react";
import MainContainer from "../../components/MainContainer";

import api from "../../services/api";
import NewsPostModal from "../../components/NewsPostModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_NEWS_POSTS") {
    const newsPosts = action.payload;
    const newItems = [];

    if (isArray(newsPosts)) {
      newsPosts.forEach((post) => {
        const index = state.findIndex((u) => u.id === post.id);
        if (index !== -1) {
          state[index] = post;
        } else {
          newItems.push(post);
        }
      });
    }

    return [...state, ...newItems];
  }

  if (action.type === "UPDATE_NEWS_POST") {
    const post = action.payload;
    const index = state.findIndex((u) => u.id === post.id);

    if (index !== -1) {
      state[index] = post;
      return [...state];
    } else {
      return [post, ...state];
    }
  }

  if (action.type === "DELETE_NEWS_POST") {
    const postId = action.payload;
    const index = state.findIndex((u) => u.id === postId);
    if (index !== -1) {
      state.splice(index, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const GRID_COLUMNS = "60px 1fr 150px 100px 120px";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#1e293b",
  },
  buttonsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  addButton: {
    height: 36,
    minWidth: 0,
    padding: "0 14px",
    borderRadius: 10,
    textTransform: "none",
    fontSize: "0.875rem",
    fontWeight: 500,
    boxShadow: "none",
    "& .MuiButton-startIcon": {
      marginRight: 6,
    },
    "&:hover": {
      boxShadow: "none",
    },
  },
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: "transparent",
    boxShadow: "none",
    ...theme.scrollbarStyles,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.02)",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(91, 124, 255, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.08)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        background:
          theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.04)",
        border:
          theme.palette.type === "dark"
            ? "1px solid rgba(91, 124, 255, 0.3)"
            : "1px solid rgba(0, 0, 0, 0.12)",
      },
      "&.Mui-focused": {
        background:
          theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.02)",
        border:
          theme.palette.type === "dark"
            ? "1px solid rgba(91, 124, 255, 0.5)"
            : "1px solid #3D5CFD",
        boxShadow:
          theme.palette.type === "dark"
            ? "0 0 0 1px rgba(91, 124, 255, 0.15)"
            : "0 0 0 1px rgba(61, 92, 253, 0.1)",
      },
      "& fieldset": {
        border: "none",
      },
    },
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginTop: theme.spacing(1),
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: "center",
    padding: "12px 20px",
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(0, 0, 0, 0.08)",
  },
  listHeaderText: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.45)"
        : "#94a3b8",
  },
  listRow: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: "center",
    padding: "14px 20px",
    borderRadius: "12px",
    transition: "background 0.15s ease",
    cursor: "default",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(91, 124, 255, 0.06)"
          : "rgba(61, 92, 253, 0.04)",
    },
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.04)"
        : "1px solid rgba(0, 0, 0, 0.04)",
  },
  postId: {
    fontSize: "0.8rem",
    fontWeight: 500,
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.4)"
        : "#94a3b8",
    fontVariantNumeric: "tabular-nums",
  },
  postNameCell: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  postTitle: {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#e5e5e5" : "#1e293b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  postAuthor: {
    fontSize: "0.8rem",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.45)"
        : "#94a3b8",
    fontWeight: 400,
  },
  activeStatus: {
    background:
      theme.palette.type === "dark"
        ? "rgba(34, 197, 94, 0.15)"
        : "rgba(34, 197, 94, 0.1)",
    color: theme.palette.type === "dark" ? "#86efac" : "#16a34a",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(34, 197, 94, 0.3)"
        : "1px solid rgba(34, 197, 94, 0.2)",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  inactiveStatus: {
    background:
      theme.palette.type === "dark"
        ? "rgba(156, 163, 175, 0.15)"
        : "rgba(156, 163, 175, 0.1)",
    color: theme.palette.type === "dark" ? "#d1d5db" : "#6b7280",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(156, 163, 175, 0.3)"
        : "1px solid rgba(156, 163, 175, 0.2)",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  actionsCell: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
  iconButton: {
    padding: 6,
    borderRadius: "8px",
    transition: "all 0.15s ease",
  },
  editButton: {
    color: theme.palette.type === "dark" ? "#93c5fd" : "#3b82f6",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(59, 130, 246, 0.15)"
          : "rgba(59, 130, 246, 0.08)",
    },
  },
  deleteButton: {
    color: theme.palette.type === "dark" ? "#fca5a5" : "#ef4444",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(239, 68, 68, 0.15)"
          : "rgba(239, 68, 68, 0.08)",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6, 2),
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.35)"
        : "#94a3b8",
    fontSize: "0.95rem",
  },
}));

const NewsPosts = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedNewsPost, setSelectedNewsPost] = useState(null);
  const [deletingNewsPost, setDeletingNewsPost] = useState(null);
  const [newsPostModalOpen, setNewsPostModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [newsPosts, dispatch] = useReducer(reducer, []);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        toast.error("Acesso restrito a super administradores.");
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchNewsPosts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-newspost`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_NEWS_POST", payload: data.record });
      }
    });

    socket.on(`company-${companyId}-newspost`, (data) => {
      if (data.action === "delete") {
        dispatch({ type: "DELETE_NEWS_POST", payload: +data.id });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [socketManager, user.companyId]);

  const fetchNewsPosts = async () => {
    try {
      const { data } = await api.get("/news-posts/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_NEWS_POSTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenNewsPostModal = () => {
    setSelectedNewsPost(null);
    setNewsPostModalOpen(true);
  };

  const handleCloseNewsPostModal = () => {
    setSelectedNewsPost(null);
    setNewsPostModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditNewsPost = (post) => {
    setSelectedNewsPost(post);
    setNewsPostModalOpen(true);
  };

  const handleDuplicateNewsPost = async (post) => {
    try {
      await api.post("/news-posts", {
        title: `${post.title} (cópia)`,
        text: post.text || "",
        status: false,
      });
      toast.success("Novidade duplicada com sucesso!");
      dispatch({ type: "RESET" });
      setPageNumber(1);
      fetchNewsPosts();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteNewsPost = async (post) => {
    try {
      if (post.mediaName)
        await api.delete(`/news-posts/${post.id}/media-upload`);

      await api.delete(`/news-posts/${post.id}`);

      toast.success("Novidade excluída com sucesso!");
    } catch (err) {
      toastError(err);
    }
    setDeletingNewsPost(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const sortedPosts = useMemo(
    () => newsPosts.slice().sort((a, b) => b.id - a.id),
    [newsPosts]
  );

  return (
    <MainContainer className={classes.root}>
      <ConfirmationModal
        title={
          deletingNewsPost &&
          `Excluir "${deletingNewsPost.title}"?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteNewsPost(deletingNewsPost)}
      >
        Tem certeza que deseja excluir esta novidade?
      </ConfirmationModal>
      <NewsPostModal
        resetPagination={() => {
          setPageNumber(1);
          fetchNewsPosts();
        }}
        open={newsPostModalOpen}
        onClose={handleCloseNewsPostModal}
        aria-labelledby="form-dialog-title"
        newsPostId={selectedNewsPost && selectedNewsPost.id}
      />

      <div className={classes.header}>
        <Typography className={classes.title}>
          Novidades ({newsPosts.length})
        </Typography>
        <div className={classes.buttonsWrapper}>
          <TextField
            placeholder="Buscar novidade..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchField}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    style={{
                      color:
                        theme.palette.type === "dark"
                          ? "rgba(255, 255, 255, 0.5)"
                          : "#64748b",
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenNewsPostModal}
            className={classes.addButton}
            startIcon={<Plus size={15} strokeWidth={1.75} />}
          >
            Nova Novidade
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <div className={classes.listHeader}>
          <Typography className={classes.listHeaderText}>ID</Typography>
          <Typography className={classes.listHeaderText}>Título</Typography>
          <Typography className={classes.listHeaderText}>Autor</Typography>
          <Typography className={classes.listHeaderText}>Status</Typography>
          <Typography
            className={classes.listHeaderText}
            style={{ textAlign: "right" }}
          >
            Ações
          </Typography>
        </div>

        <div className={classes.listContainer}>
          {sortedPosts.length === 0 && !loading ? (
            <Typography className={classes.emptyState}>
              Nenhuma novidade encontrada
            </Typography>
          ) : (
            sortedPosts.map((post) => (
              <div key={post.id} className={classes.listRow}>
                <Typography className={classes.postId}>
                  #{post.id}
                </Typography>

                <div className={classes.postNameCell}>
                  <Typography className={classes.postTitle}>
                    {post.title}
                  </Typography>
                </div>

                <Typography className={classes.postAuthor}>
                  {post.author || post.user?.name || "Desconhecido"}
                </Typography>

                <div>
                  <Chip
                    label={post.status ? "Ativo" : "Inativo"}
                    className={
                      post.status
                        ? classes.activeStatus
                        : classes.inactiveStatus
                    }
                    size="small"
                  />
                </div>

                <div className={classes.actionsCell}>
                  <IconButton
                    size="small"
                    className={`${classes.iconButton} ${classes.editButton}`}
                    onClick={() => handleEditNewsPost(post)}
                    title="Editar"
                  >
                    <Pencil size={16} strokeWidth={1.5} />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={`${classes.iconButton} ${classes.editButton}`}
                    onClick={() => handleDuplicateNewsPost(post)}
                    title="Duplicar"
                  >
                    <Copy size={16} strokeWidth={1.5} />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={`${classes.iconButton} ${classes.deleteButton}`}
                    onClick={() => {
                      setConfirmModalOpen(true);
                      setDeletingNewsPost(post);
                    }}
                    title="Deletar"
                  >
                    <Trash size={16} strokeWidth={1.5} />
                  </IconButton>
                </div>
              </div>
            ))
          )}
        </div>
      </Paper>
    </MainContainer>
  );
};

export default NewsPosts;
